import type { LanguageId } from '../pipeline/types';
import { chunkFile, type Chunk } from './chunking';
import { contentHash } from './content-hash';
import { getCachedEmbedding, saveCachedEmbedding } from '../db/embeddings-repo';
import { embedText, isEmbedderReady } from './embeddings';

export interface ChunkRef extends Chunk {
  contentHash: string;
}

const chunksByFile = new Map<string, ChunkRef[]>();

/**
 * Chunks a file and ensures each chunk has a cached embedding, computing and
 * storing any that are missing. Runs entirely in the background (fire-and-
 * forget from main.ts on file load/save) — the A3 pipeline stage only ever
 * reads whatever's already cached by the time a chat request happens, so it
 * never blocks on embedding work.
 */
export async function updateChunkIndexForFile(
  path: string,
  content: string,
  languageId: LanguageId | undefined,
): Promise<void> {
  const chunks = await chunkFile(path, content, languageId);
  const refs: ChunkRef[] = [];

  for (const chunk of chunks) {
    const hash = await contentHash(chunk.text);
    refs.push({ ...chunk, contentHash: hash });

    if (!isEmbedderReady()) continue; // background pass will catch up once the model loads
    const cached = await getCachedEmbedding(hash);
    if (cached) continue;
    try {
      const vector = await embedText(chunk.text);
      await saveCachedEmbedding({ contentHash: hash, vector: Array.from(vector), path, text: chunk.text });
    } catch {
      // fail open — this chunk just won't be rankable until a later retry
    }
  }

  chunksByFile.set(path, refs);
}

export function removeFileFromChunkIndex(path: string): void {
  chunksByFile.delete(path);
}

export function clearChunkIndex(): void {
  chunksByFile.clear();
}

export function allChunkRefs(): ChunkRef[] {
  return Array.from(chunksByFile.values()).flat();
}
